/* session-rail.js — left-side session switcher rail
 *
 * renderRail()  Builds/updates the #sessionRail <aside> with one row per
 *               known session, a status dot, title, optional cost, and a
 *               "+" button to spawn a new session.
 *
 * Refresh triggers
 *   - explicit call (boot, after sendMessage, WS agent events)
 *   - 5-second interval while any session is in the "running" state
 */

import { state, setActiveSession, spawnSession } from "./state.js";
import { getAuthMode, getActiveConnection, setActiveConversation } from "./auth.js";
import { reconnectWs } from "./ws.js";
import { api } from "./api.js";

const _isHosted = () => getAuthMode() === "hosted";

// Replay a conversation's persisted messages when switching to it (hosted). No-op if the session
// already has a live/loaded buffer, or if the conversation has no stored messages yet.
async function _loadHostedMessages(convId) {
  const sess = state.sessions[convId];
  if (sess && sess.messages && sess.messages.length) return;
  const cid = getActiveConnection();
  if (!cid) return;
  try {
    const data = await api.connectionMessages(cid, convId);
    if (data && data.messages && data.messages.length) {
      // setActiveSession ran -> targets convId. preserveLive: the conversation
      // may have a run streaming into it — don't clobber the live turn.
      window.loadConversationMessages?.(data.messages, { preserveLive: true });
    }
  } catch { /* leave empty */ }
}

// ---------------------------------------------------------------------------
// Status → CSS icon class + colour modifier
// ---------------------------------------------------------------------------

function statusIcon(status) {
  switch (status) {
    case "running":
      return { cls: "ic ic-loader", mod: "rail-dot--running" };
    case "awaiting_permission":
    case "awaiting_question":
      return { cls: "ic ic-circle-pause", mod: "rail-dot--waiting" };
    case "idle":
      return { cls: "ic ic-check", mod: "rail-dot--idle" };
    default:
      return { cls: "", mod: "rail-dot--unknown" };
  }
}

// ---------------------------------------------------------------------------
// Internal poll state
// ---------------------------------------------------------------------------

let _pollTimer = null;

function _scheduleOrCancelPoll() {
  const hasRunning = Object.values(_sessionMeta).some(m => m.status === "running");
  if (hasRunning && !_pollTimer) {
    _pollTimer = setInterval(() => {
      _fetchAndMerge().then(renderRail);
    }, 5000);
  } else if (!hasRunning && _pollTimer) {
    clearInterval(_pollTimer);
    _pollTimer = null;
  }
}

// ---------------------------------------------------------------------------
// Session metadata cache  { [convId]: { status, title, cost } }
// ---------------------------------------------------------------------------

let _sessionMeta = {};

async function _fetchAndMerge() {
  // Always ensure the active session has at least an idle entry before any async work
  if (!_sessionMeta[state.activeSessionId]) {
    _sessionMeta[state.activeSessionId] = { status: "idle", title: null, cost: null };
  }
  if (_isHosted()) {
    // Hosted: the rail mirrors THIS connection's server conversations (the single-operator
    // /api/sessions surface is 404 in hosted).
    const cid = getActiveConnection();
    if (cid) {
      try {
        const data = await api.connectionConversations(cid);
        const next = {};
        for (const c of (data.conversations || [])) {
          next[c.id] = {
            status: _sessionMeta[c.id]?.status || "idle",
            title: c.title || _sessionMeta[c.id]?.title || null,
            cost: _sessionMeta[c.id]?.cost ?? null,
          };
        }
        if (state.activeSessionId && !next[state.activeSessionId]) {
          next[state.activeSessionId] =
            _sessionMeta[state.activeSessionId] || { status: "idle", title: null, cost: null };
        }
        _sessionMeta = next;
      } catch { /* degrade gracefully */ }
    }
    return;
  }
  try {
    const res = await fetch("/api/sessions");
    if (!res.ok) return;
    const data = await res.json();
    const serverSessions = data.sessions || [];
    // Merge server-known sessions into meta cache
    for (const s of serverSessions) {
      const id = s.conversation_id || s.id;
      if (!id) continue;
      _sessionMeta[id] = {
        status: s.status || "idle",
        title: s.title || _sessionMeta[id]?.title || null,
        cost: s.cost_usd != null ? s.cost_usd : (_sessionMeta[id]?.cost ?? null),
      };
    }
    // Ensure every locally-known session has a meta entry
    for (const id of Object.keys(state.sessions)) {
      if (!_sessionMeta[id]) {
        _sessionMeta[id] = { status: "idle", title: null, cost: null };
      }
    }
    // Ensure the active session always exists after merge
    if (!_sessionMeta[state.activeSessionId]) {
      _sessionMeta[state.activeSessionId] = { status: "idle", title: null, cost: null };
    }
  } catch {
    // Fail silently — rail degrades gracefully when server is unreachable
  }
}

// ---------------------------------------------------------------------------
// Derive a display title for a session
// ---------------------------------------------------------------------------

function _sessionTitle(convId) {
  // Prefer the cached server-provided title
  const meta = _sessionMeta[convId];
  if (meta?.title) return meta.title;

  // Fall back to the first user message text in local state
  const sess = state.sessions[convId];
  if (sess) {
    const first = (sess.messages || []).find(m => m.role === "user");
    if (first?.text) {
      const text = first.text.trim().replace(/\s+/g, " ");
      return text.length > 40 ? text.slice(0, 37) + "…" : text;
    }
  }

  return "New session";
}

// ---------------------------------------------------------------------------
// Render
// ---------------------------------------------------------------------------

export function renderRail() {
  const rail = document.getElementById("sessionRail");
  if (!rail) return;

  // Gather the union of all known session IDs
  const localIds = Object.keys(state.sessions);
  const metaIds = Object.keys(_sessionMeta);
  const allIds = [...new Set([...localIds, ...metaIds, state.activeSessionId])];

  // Build session rows
  const rows = allIds.map(id => {
    const _m = _sessionMeta[id];
    const meta = { status: (_m?.status || "idle"), cost: _m?.cost ?? null };
    const isActive = id === state.activeSessionId;
    const { cls, mod } = statusIcon(meta.status);
    const title = _sessionTitle(id);
    const costHtml = meta.cost != null
      ? `<span class="rail-row-cost">$${Number(meta.cost).toFixed(4)}</span>`
      : "";
    const dotHtml = cls
      ? `<i class="${cls} rail-dot ${mod}"></i>`
      : `<span class="rail-dot ${mod}"></span>`;

    return `<button
      class="rail-row${isActive ? " rail-row--active" : ""}"
      data-conv-id="${_esc(id)}"
      title="${_esc(title)}"
      type="button"
    >
      ${dotHtml}
      <span class="rail-row-title">${_esc(title)}</span>
      ${costHtml}
    </button>`;
  }).join("");

  // Hosted: rename/delete act on the active conversation (server-issued). Local mode keeps just "+".
  const hostedActions = _isHosted() ? `
      <button class="rail-new-btn" id="railRenameBtn" title="Rename conversation" type="button">&#9998;</button>
      <button class="rail-new-btn" id="railDeleteBtn" title="Delete conversation" type="button">&#10005;</button>` : "";
  rail.innerHTML = `
    <div class="rail-header">
      <span class="rail-label">Sessions</span>
      ${hostedActions}
      <button class="rail-new-btn" id="railNewBtn" title="New session" type="button">
        <i class="ic ic-plus" aria-hidden="true"></i>
      </button>
    </div>
    <div class="rail-rows">${rows}</div>
  `;

  // Wire click handlers
  rail.querySelectorAll(".rail-row").forEach(btn => {
    btn.addEventListener("click", async () => {
      const convId = btn.dataset.convId;
      if (!convId || convId === state.activeSessionId) return;
      setActiveSession(convId);
      if (_isHosted()) {
        setActiveConversation(convId);  // rebind the run socket to the chosen conversation
        reconnectWs();
        await _loadHostedMessages(convId);  // replay this conversation's prior turns
      }
      renderRail();
      // Re-render the chat stage for the newly active session.
      // renderStage is exported from chat.js and exposed on window by main.js
      // to avoid a circular ES module import (session-rail → main → session-rail).
      window.renderStage?.(convId);
      const input = document.getElementById("promptInput");
      if (input) input.focus();
    });
  });

  document.getElementById("railNewBtn")?.addEventListener("click", async () => {
    if (_isHosted()) {
      const cid = getActiveConnection();
      if (!cid) return;
      try {
        const conv = await api.createConnectionConversation(cid, null);  // server-issued id
        _sessionMeta[conv.id] = { status: "idle", title: conv.title || null, cost: null };
        setActiveConversation(conv.id);
        setActiveSession(conv.id);
        reconnectWs();
        renderRail();
        window.renderStage?.(conv.id);
        document.getElementById("promptInput")?.focus();
      } catch (e) {
        console.error("create conversation failed", e);
      }
      return;
    }
    spawnSession();
    const newConvId = state.activeSessionId;
    // Update meta for the new session immediately
    _sessionMeta[newConvId] = { status: "idle", title: null, cost: null };
    renderRail();
    // Re-render stage for the new (empty) session.
    window.renderStage?.(newConvId);
    const input = document.getElementById("promptInput");
    if (input) input.focus();
  });

  // Hosted-only: rename / delete the active conversation.
  document.getElementById("railRenameBtn")?.addEventListener("click", async () => {
    const cid = getActiveConnection();
    const coid = state.activeSessionId;
    if (!cid || !coid) return;
    const title = window.prompt("Rename conversation", _sessionMeta[coid]?.title || "");
    if (title == null) return;
    try {
      await api.renameConnectionConversation(cid, coid, title);
      _sessionMeta[coid] = { ...(_sessionMeta[coid] || {}), title };
      renderRail();
    } catch (e) {
      console.error("rename conversation failed", e);
    }
  });

  document.getElementById("railDeleteBtn")?.addEventListener("click", async () => {
    const cid = getActiveConnection();
    const coid = state.activeSessionId;
    if (!cid || !coid) return;
    if (!window.confirm("Delete this conversation? This can't be undone.")) return;
    try {
      await api.deleteConnectionConversation(cid, coid);  // also tears down its sandbox Machine
    } catch (e) {
      console.error("delete conversation failed", e);
      return;
    }
    delete _sessionMeta[coid];
    delete state.sessions[coid];
    // Switch to another conversation if one remains, else mint a fresh one.
    const remaining = Object.keys(_sessionMeta);
    let nextId = remaining[0];
    if (!nextId) {
      try {
        const conv = await api.createConnectionConversation(cid, null);
        _sessionMeta[conv.id] = { status: "idle", title: conv.title || null, cost: null };
        nextId = conv.id;
      } catch (e) {
        console.error("create conversation after delete failed", e);
        return;
      }
    }
    setActiveConversation(nextId);
    setActiveSession(nextId);
    reconnectWs();
    renderRail();
    window.renderStage?.(nextId);
  });

  _scheduleOrCancelPoll();
}

// ---------------------------------------------------------------------------
// Boot — initial fetch + first render
// ---------------------------------------------------------------------------

export async function initRail() {
  await _fetchAndMerge();
  renderRail();
  // Re-render after a tick so any synchronous boot mutations in other modules
  // (e.g. main.js DOMContentLoaded handlers) are reflected.
  setTimeout(() => {
    _fetchAndMerge().then(renderRail);
  }, 0);
}

// ---------------------------------------------------------------------------
// Called by main.js after sendMessage to reflect the new running state
// ---------------------------------------------------------------------------

export function onSessionRunning(convId) {
  if (!_sessionMeta[convId]) {
    _sessionMeta[convId] = { status: "idle", title: null, cost: null };
  }
  _sessionMeta[convId].status = "running";
  renderRail();
}

// ---------------------------------------------------------------------------
// Called by main.js on WS agent events that change session status
// ---------------------------------------------------------------------------

export function onAgentEvent(convId, eventType) {
  if (!_sessionMeta[convId]) {
    _sessionMeta[convId] = { status: "idle", title: null, cost: null };
  }
  switch (eventType) {
    case "agent_status":
      _sessionMeta[convId].status = "running";
      break;
    case "agent_final":
    case "agent_error":
      _sessionMeta[convId].status = "idle";
      break;
    case "agent_permission_request":
    case "agent_user_question":
      _sessionMeta[convId].status = "awaiting_permission";
      break;
  }
  renderRail();
}

// ---------------------------------------------------------------------------
// Called by main.js on the server's conversation_titled frame
// ---------------------------------------------------------------------------

/** Adopt a server-assigned title for one conversation and repaint the rail.
 *  The rail's own poller only runs while a session is running, and the title is
 *  written seconds AFTER the run ends — so without this the rail would keep
 *  showing the first-message fallback until the next full refresh. */
export function onConversationTitled(convId, title) {
  if (!convId || !title) return;
  if (!_sessionMeta[convId]) {
    _sessionMeta[convId] = { status: "idle", title: null, cost: null };
  }
  _sessionMeta[convId].title = title;
  renderRail();
}

// ---------------------------------------------------------------------------
// Internal HTML escaping
// ---------------------------------------------------------------------------

function _esc(str) {
  if (str == null) return "";
  const d = document.createElement("div");
  d.textContent = String(str);
  return d.innerHTML;
}

// ---------------------------------------------------------------------------
// Auto-init removed: main.js imports session-rail.js and calls initRail()
// explicitly via init() → connectWs(), so a standalone auto-init here
// would double-initialise the rail. The standalone <script type="module">
// tag in index.html still loads this module but defers to main.js for init.
// ---------------------------------------------------------------------------
